/**
 * Pure helpers for the portal data grid (ADR-0010 G3/G6 pinning, G4 reorder).
 *
 * Kept out of the renderer so the column-ordering logic is unit-testable
 * without mounting a `data_list`.
 */

/** Which edge a column is frozen to (G3 left, G6 right) — or not pinned. */
export type PinSide = "left" | "right";

/** Per-user, per-list column preferences (G2 hide / G3+G6 pin / G4 order /
 *  G7 width), persisted in localStorage and seeded from the admin defaults.
 *  `pinned` maps a column to the edge it's frozen to; `widths` maps a column to
 *  a user-set pixel width (absent = natural/auto). */
export interface ColumnPrefs {
  hidden: string[];
  pinned: Record<string, PinSide>;
  order: string[];
  widths: Record<string, number>;
  /** User-chosen rows-per-page (G9); absent = the admin's `page_size`. */
  pageSize?: number;
}

/** A user-named snapshot of `ColumnPrefs` (G10 saved views): the hide/pin/
 *  order/width/page-size state under a friendly label, so the portal user can
 *  flip between "Compact", "Finance", … layouts. Persisted per-user alongside
 *  the live prefs. */
export interface SavedView {
  name: string;
  prefs: ColumnPrefs;
}

/** Load the saved-views list from localStorage (tolerant of junk / SSR). */
export function loadViews(key: string): SavedView[] {
  if (!key || typeof localStorage === "undefined") return [];
  try {
    const raw = localStorage.getItem(key);
    const v = raw ? JSON.parse(raw) : [];
    return Array.isArray(v) ? (v as SavedView[]).filter((x) => x && typeof x.name === "string" && x.prefs) : [];
  } catch {
    return [];
  }
}

/** Persist the saved-views list (no-op when keyless / SSR). */
export function saveViews(key: string, views: SavedView[]): void {
  if (!key || typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(key, JSON.stringify(views));
  } catch {
    /* quota / private mode — saved views are best-effort */
  }
}

/** Upsert a view by name (case-sensitive), keeping list order stable. */
export function upsertView(views: SavedView[], view: SavedView): SavedView[] {
  const i = views.findIndex((v) => v.name === view.name);
  if (i < 0) return [...views, view];
  const next = views.slice();
  next[i] = view;
  return next;
}

/** The curated rows-per-page choices — the SINGLE source of truth shared by the
 *  admin `page_size` dropdown (which also offers "All" = 0) and the runtime
 *  selector. Keep these aligned so the runtime never offers a size the admin
 *  couldn't have set. The admin's chosen value is merged in (covering legacy
 *  non-standard values); everything is capped to the server's 200. */
export const PAGE_SIZE_CHOICES = [25, 50, 100];

/** Build the runtime page-size options: the curated choices + the admin default
 *  (so it's always present and selectable), sorted/de-duped, capped at 200. */
export function pageSizeOptions(adminPageSize: number): number[] {
  return Array.from(new Set([adminPageSize, ...PAGE_SIZE_CHOICES]))
    .filter((n) => n > 0 && n <= 200)
    .sort((a, b) => a - b);
}

/** Smallest a user can drag a column (G7). */
export const MIN_COL_WIDTH = 64;

/** Stringify one cell for CSV (G8): null → empty, bool → true/false, object →
 *  JSON; then RFC-4180-quote if it contains a comma / quote / newline. */
function csvCell(v: unknown): string {
  if (v == null) return "";
  let s: string;
  if (typeof v === "boolean") s = v ? "true" : "false";
  else if (typeof v === "object") s = JSON.stringify(v);
  else s = String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** Build a CSV string from the given columns (header + order) and rows (G8). */
export function toCsv(columns: string[], rows: Record<string, unknown>[]): string {
  const head = columns.map(csvCell).join(",");
  if (rows.length === 0) return head;
  const body = rows.map((r) => columns.map((c) => csvCell(r[c])).join(",")).join("\r\n");
  return `${head}\r\n${body}`;
}

/** Trigger a browser download of `text` as a file (G8). No-op outside a DOM
 *  (SSR/tests). A UTF-8 BOM is prepended so Excel reads accents correctly. */
export function downloadTextFile(filename: string, text: string, mime = "text/csv;charset=utf-8"): void {
  if (typeof document === "undefined" || typeof URL === "undefined" || !URL.createObjectURL) return;
  const blob = new Blob(["﻿", text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Apply a user-chosen column order on top of the full column set. Columns named
 * in `order` lead (in that order); any column not listed (e.g. added after the
 * order was saved) keeps its natural position at the end. An empty order is a
 * no-op (natural order preserved).
 */
export function applyColumnOrder(columns: string[], order: string[]): string[] {
  if (order.length === 0) return columns;
  const lead = order.filter((c) => columns.includes(c));
  const leadSet = new Set(lead);
  const rest = columns.filter((c) => !leadSet.has(c));
  return [...lead, ...rest];
}

/** Move the item at `from` to `to`, returning a new array (drag-reorder). */
export function moveItem<T>(items: T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) return items;
  const next = [...items];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

/** Fixed width (px) of a pinned column. Pinned columns get a deterministic
 *  width so their sticky offsets are a plain cumulative sum — no DOM
 *  measurement needed (the Excel/AG-Grid freeze-pane model). */
export const PINNED_COL_WIDTH = 176;

/** Fixed width (px) of the row-actions column when it is pinned to the right
 *  (only happens alongside right-pinned data columns). */
export const ACTIONS_COL_WIDTH = 96;

/**
 * Reorder columns so left-pinned lead and right-pinned trail (each keeping its
 * relative order), with the unpinned columns in the middle. Moving pinned
 * columns to the edges makes their sticky offsets a plain cumulative sum.
 */
export function orderPinned(columns: string[], pinOf: (col: string) => PinSide | null): string[] {
  const left = columns.filter((c) => pinOf(c) === "left");
  const right = columns.filter((c) => pinOf(c) === "right");
  const mid = columns.filter((c) => pinOf(c) == null);
  return [...left, ...mid, ...right];
}

/**
 * Sticky placement for a column once `orderPinned`-ordered: left-pinned columns
 * offset from the left by the cumulative width of preceding left-pins;
 * right-pinned columns offset from the right (the rightmost sits at `rightBase`,
 * e.g. a pinned actions column's width). `widthOf` gives each pinned column's
 * width (G7 lets the user resize pins); it defaults to `PINNED_COL_WIDTH`.
 * Returns null for unpinned columns.
 */
export function pinnedOffset(
  orderedColumns: string[],
  pinOf: (col: string) => PinSide | null,
  col: string,
  rightBase = 0,
  widthOf?: (col: string) => number,
): { side: PinSide; px: number } | null {
  const side = pinOf(col);
  if (!side) return null;
  const w = (c: string) => widthOf?.(c) ?? PINNED_COL_WIDTH;
  if (side === "left") {
    let px = 0;
    for (const c of orderedColumns) {
      if (c === col) return { side: "left", px };
      if (pinOf(c) === "left") px += w(c);
    }
    return null;
  }
  let px = rightBase;
  for (let i = orderedColumns.length - 1; i >= 0; i--) {
    const c = orderedColumns[i];
    if (c === col) return { side: "right", px };
    if (pinOf(c) === "right") px += w(c);
  }
  return null;
}
